/**
 * OutputFileBlock — downloadable file chip for files produced by tool
 * execution (output_files on mission.tool.result).
 *
 * Chat-ui counterpart of the designer's FileDownloadBlock
 * (admin-ui ExecutionChat.tsx). Downloads go through the governance
 * agent-host file proxy, so they work for BYOH-hosted missions only —
 * without a hostId the chip has no download button and says why.
 * A failed download says why by its HTTP status (outputFileTexts.ts).
 */

import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@agion/ui'
import { hostsApi } from '../../services/api'
import type { ToolOutputFile } from '@agion/mission-stream'
import { downloadFailureText, httpStatusOf, outputFileText } from './outputFileTexts'

export type { ToolOutputFile } from '@agion/mission-stream'

const ICON_MAP: Record<string, { label: string; color: string }> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    label: 'XLSX',
    color: 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300',
  },
  'application/pdf': {
    label: 'PDF',
    color: 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  },
  'text/csv': {
    label: 'CSV',
    color: 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300',
  },
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function OutputFileBlock({ file, hostId }: { file: ToolOutputFile; hostId?: string }) {
  const { t } = useTranslation(['chat'])
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const icon = ICON_MAP[file.contentType] ?? {
    label: 'FILE',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
  }
  // Downloads go through the host-file proxy, which needs the mission's host.
  const noHostText = hostId ? null : outputFileText(t, 'noHost')

  const handleDownload = useCallback(async () => {
    if (downloading || !hostId) return
    setDownloading(true)
    setDownloadError(null)
    try {
      const blob = await hostsApi.downloadHostFile(hostId, file.fileId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      const status = httpStatusOf(err)
      console.error('[OutputFileBlock] File download failed', { status: status ?? null, fileId: file.fileId, hostId })
      setDownloadError(outputFileText(t, downloadFailureText(status)))
    } finally {
      setDownloading(false)
    }
  }, [file, hostId, downloading, t])

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      data-testid="output-file-block"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${icon.color}`}
      >
        {icon.label}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {file.name}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {downloadError ?? formatSize(file.size)}
        </div>
        {noHostText && (
          <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="output-file-no-host">
            {noHostText}
          </div>
        )}
      </div>
      {hostId && (
        <Button variant="secondary" size="sm" onClick={handleDownload} disabled={downloading}>
          {downloading
            ? t('chat:outputFile.downloading', 'Downloading…')
            : t('chat:outputFile.download', 'Download')}
        </Button>
      )}
    </div>
  )
}

export default OutputFileBlock
